import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTokens } from "../hooks/useTokens";
import { useProfileInfo } from "../contexts/ProfileInfoContext";
import TokenPurchaseModal from "./TokenPurchaseModal";
import MockInterviewModal from "./MockInterviewModal";
import useCVAnalysis from "../hooks/useCVAnalysis";
import { isAdmin } from "../utils/adminUtils";
import { supabase } from "../supabase";

// ToolTip component
const ToolTip = ({ toolTipLabel }) => {
    return (
        <div className="absolute left-full ml-3 top-1/2 -translate-y-1/2 z-10 opacity-0 group-hover:opacity-100 bg-[#1E1E1E]/80 text-white text-sm py-1 px-3 rounded-md whitespace-nowrap shadow-lg transition-all duration-200">
            {toolTipLabel}
            {/* Arrow */}
            <div className="absolute left-[-5px] top-1/2 -translate-y-1/2 w-0 h-0 border-t-4 border-b-4 border-r-4 border-t-transparent border-b-transparent border-r-[#1E1E1E]/80"></div>
        </div>
    );
};

// NavItem component
const NavItem = ({
    icon,
    label,
    tooltip,
    onClick,
    isSidebarOpen,
    comingSoon,
    active = false,
}) => {
    return (
        <li
            className={`relative group flex items-center py-2 rounded-lg cursor-pointer text-white
            ${comingSoon ? "" : ""}
            ${label ? "gap-4 px-3" : "justify-center"}
            `}
            onClick={onClick}
            aria-disabled={comingSoon}
            style={comingSoon ? { cursor: "not-allowed", opacity: 0.5 } : {}}
        >
            <div className="w-5 h-5 flex items-center justify-center">
                {icon}
            </div>
            {label && <span className="text-white leading-5 font-normal whitespace-nowrap">{label}</span>}

            {/* Tooltip */}
            {!isSidebarOpen && tooltip && <ToolTip toolTipLabel={tooltip} />}

            {/* Vertical line indicator */}
            <span
                className={`
                absolute right-0 h-6 w-[4px] bg-white
                transition-opacity
                ${active ? "opacity-100" : "opacity-0 group-hover:opacity-100"}
            `}
            />
        </li>
    );
};

const EmployeeSidebar = ({ isOpen, onToggle, isMobile }) => {
    const { user, signOut } = useAuth();
    const { tokens, loading: tokensLoading, fetchTokens } = useTokens();
    const navigate = useNavigate();
    const location = useLocation();
    const [showTokenModal, setShowTokenModal] = useState(false);
    const [showMockInterviewModal, setShowMockInterviewModal] = useState(false);
    const [mockInterviewPaid, setMockInterviewPaid] = useState(false);
    const [mockInterviewConsented, setMockInterviewConsented] = useState(false);
    const { getLatestAnalysis } = useCVAnalysis(user);
    const { profileInfo } = useProfileInfo();

    // CRITICAL: Role validation - Employee sidebar should ONLY render for Employees
    useEffect(() => {
        if (!user) return;
        if (profileInfo && profileInfo.role !== 'Employee') {
            console.warn(`[EmployeeSidebar] Access denied: User role is ${profileInfo.role}, redirecting`);
            if (profileInfo.role === 'Employer') {
                navigate("/employer/dashboard", { replace: true });
            } else {
                navigate("/role-selection", { replace: true });
            }
        }
    }, [user, profileInfo, navigate]);

    // Check if user has paid and consented for mock interview
    useEffect(() => {
        const checkMockInterviewStatus = async () => {
            if (!user?.id) return;

            try {
                const { data, error } = await supabase
                    .from('user_tokens')
                    .select('mock_interview_paid, mock_interview_consent')
                    .eq('user_id', user.id)
                    .single();

                if (!error && data) {
                    setMockInterviewPaid(data.mock_interview_paid || false);
                    setMockInterviewConsented(data.mock_interview_consent || false);
                } else {
                    setMockInterviewPaid(false);
                    setMockInterviewConsented(false);
                }
            } catch (err) {
                console.error("Error checking mock interview status:", err);
                setMockInterviewPaid(false);
                setMockInterviewConsented(false);
            }
        };

        checkMockInterviewStatus();
    }, [user]);

    const handleCVAnalyzerClick = async (e) => {
        e.preventDefault();
        // CRITICAL: Double-check role before navigation
        if (profileInfo?.role !== 'Employee') {
            console.warn('[EmployeeSidebar] Navigation blocked: User is not an Employee');
            return;
        }
        if (user) {
            const latestAnalysis = await getLatestAnalysis();
            if (latestAnalysis && latestAnalysis.cv_analysis_results) {
                navigate(`/employee/cv-analysis-results?id=${latestAnalysis.id}`);
            } else {
                navigate("/employee/cv-analyser");
            }
        } else {
            navigate("/employee/cv-analyser");
        }
        
        // Close sidebar on mobile after navigation
        if (isMobile) {
            onToggle();
        }
    };

    const handleTokenPurchaseSuccess = () => {
        fetchTokens();
        setShowTokenModal(false);
    };

    if (!user) {
        return null;
    }

    // CRITICAL: Don't render sidebar if user is not an Employee
    if (profileInfo && profileInfo.role !== 'Employee') {
        return null;
    }

    return (
        <nav
            className={`flex flex-col text-white ${
                isMobile ? "w-[270px]" : (!isOpen ? "w-[78px]" : "w-[270px]")
            } h-screen`}
            style={{ backgroundColor: '#1e1e1e' }}
        >
            {/* Logo section */}
            <header
                className={`flex items-center ${
                    (isMobile || isOpen) ? "gap-5 px-5 py-5" : "justify-center py-5"
                }`}
                style={{ backgroundColor: '#1e1e1e' }}
            >
                {(isMobile || isOpen) && (
                    <div className="flex items-center gap-3">
                        <img
                            alt="Doctors' Jobs Logo"
                            src="/DJ logo white.svg"
                            className="flex-shrink-0 h-8 w-auto object-contain cursor-pointer"
                            onClick={() => {
                                navigate("/");
                                if (isMobile) {
                                    onToggle();
                                }
                            }}
                        />
                    </div>
                )}
                {!isMobile && !isOpen && (
                    <img
                        alt="Open Sidebar"
                        src="/minimise-close.svg"
                        className="w-6 h-6 object-contain cursor-pointer transform scale-x-[-1]"
                        onClick={onToggle}
                    />
                )}
                {(isMobile || isOpen) && (
                    <div
                        className={`transition-transform duration-300`}
                    >
                        <img
                            onClick={onToggle}
                            alt="Minimise Close"
                            src="/minimise-close.svg"
                            className="w-6 h-6 object-contain cursor-pointer"
                        />
                    </div>
                )}
            </header>

            {/* Divider underneath header */}
            <div className="border-t border-gray-600"></div>

            {/* Main menu */}
            <div className={`flex-grow ${(!isMobile && !isOpen) ? "" : "overflow-y-auto"}`}>
                <ul
                    className={`flex flex-col gap-1 px-2 mt-5 ${
                        (!isMobile && !isOpen) ? "px-2" : ""
                    }`}
                >
                    <NavItem
                        icon={
                            <img
                                alt="Dashboard"
                                src="/House.svg"
                                className="w-5 h-5"
                            />
                        }
                        label={(isMobile || isOpen) ? "Dashboard" : ""}
                        tooltip="Dashboard"
                        isSidebarOpen={isMobile || isOpen}
                        onClick={() => {
                            // CRITICAL: Double-check role before navigation
                            if (profileInfo?.role !== 'Employee') {
                                console.warn('[EmployeeSidebar] Navigation blocked: User is not an Employee');
                                return;
                            }
                            navigate("/employee/dashboard");
                            if (isMobile) {
                                onToggle();
                            }
                        }}
                        active={location.pathname === "/employee/dashboard"}
                    />

                    <NavItem
                        icon={
                            <img
                                alt="Job Board"
                                src="/Job board icon@4x.webp"
                                className="w-5 h-5"
                            />
                        }
                        label={(isMobile || isOpen) ? "Job Board" : ""}
                        tooltip="Job Board"
                        isSidebarOpen={isMobile || isOpen}
                        onClick={() => {
                            // CRITICAL: Double-check role before navigation
                            if (profileInfo?.role !== 'Employee') {
                                console.warn('[EmployeeSidebar] Navigation blocked: User is not an Employee');
                                return;
                            }
                            navigate("/employee/job-board");
                            if (isMobile) {
                                onToggle();
                            }
                        }}
                        active={location.pathname === "/employee/job-board"}
                    />

                    <NavItem
                        icon={
                            <img
                                alt="Company Search"
                                src="/Company Search.svg"
                                className="w-5 h-5"
                            />
                        }
                        label={(isMobile || isOpen) ? "Company Search" : ""}
                        tooltip="Company Search"
                        isSidebarOpen={isMobile || isOpen}
                        onClick={() => {
                            // CRITICAL: Double-check role before navigation
                            if (profileInfo?.role !== 'Employee') {
                                console.warn('[EmployeeSidebar] Navigation blocked: User is not an Employee');
                                return;
                            }
                            navigate("/employee/company-search");
                            if (isMobile) {
                                onToggle();
                            }
                        }}
                        active={location.pathname === "/employee/company-search"}
                    />

                    <NavItem
                        icon={
                            <img
                                alt="Job Applications Tracker"
                                src="/track jobs icon.svg"
                                className="object-contain"
                                style={{ 
                                    width: '40px', 
                                    height: '40px',
                                    minWidth: '40px',
                                    minHeight: '40px'
                                }}
                            />
                        }
                        label={(isMobile || isOpen) ? "Job Applications Tracker" : ""}
                        tooltip="Job Applications Tracker"
                        isSidebarOpen={isMobile || isOpen}
                        onClick={() => {
                            // CRITICAL: Double-check role before navigation
                            if (profileInfo?.role !== 'Employee') {
                                console.warn('[EmployeeSidebar] Navigation blocked: User is not an Employee');
                                return;
                            }
                            navigate("/employee/job-applications-tracker");
                            if (isMobile) {
                                onToggle();
                            }
                        }}
                        active={location.pathname === "/employee/job-applications-tracker"}
                    />

                    {/* <NavItem
                        icon={
                            <img
                                alt="Saved Jobs"
                                src="/track jobs icon.svg"
                                className="object-contain"
                                style={{
                                    width: '40px',
                                    height: '40px',
                                    minWidth: '40px',
                                    minHeight: '40px'
                                }}
                            />
                        }
                        label={(isMobile || isOpen) ? "Saved Jobs" : ""}
                        tooltip="Saved Jobs"
                        isSidebarOpen={isMobile || isOpen}
                        onClick={() => {
                            navigate("/employee/saved-jobs");
                            if (isMobile) {
                                onToggle();
                            }
                        }}
                        active={location.pathname === "/employee/saved-jobs"}
                    /> */}

                    <NavItem
                        icon={
                            <img
                                alt="CV Analyser"
                                src="/CV analyses@4x.webp"
                                className="w-5 h-5"
                            />
                        }
                        label={(isMobile || isOpen) ? "CV Analyser" : ""}
                        tooltip="CV Analyser"
                        isSidebarOpen={isMobile || isOpen}
                        onClick={handleCVAnalyzerClick}
                        active={location.pathname === "/employee/cv-analyser" || location.pathname === "/employee/cv-analysis-results"}
                    />

                </ul>

                {/* Divider line */}
                <div className="px-4 my-4">
                    <div className="border-t border-gray-600"></div>
                </div>

                <ul
                    className={`flex flex-col gap-1 px-4 ${
                        (!isMobile && !isOpen) ? "px-2" : ""
                    }`}
                >
                    <NavItem
                        icon={
                            <img
                                alt="Mock Interview"
                                src="/interview.svg"
                                className="w-5 h-5"
                            />
                        }
                        label={(isMobile || isOpen) ? "Mock Interview" : ""}
                        tooltip="Mock Interview"
                        isSidebarOpen={isMobile || isOpen}
                        onClick={async () => {
                            // CRITICAL: Double-check role before navigation
                            if (profileInfo?.role !== 'Employee') {
                                console.warn('[EmployeeSidebar] Navigation blocked: User is not an Employee');
                                return;
                            }
                            // Check if user has paid AND consented
                            if (mockInterviewPaid && mockInterviewConsented) {
                                // For paid and consented users, navigate and close sidebar on mobile
                                if (isMobile) {
                                    onToggle();
                                }
                                navigate("/employee/practice-interview-questions");
                            } else {
                                // IMPORTANT: Do NOT close the mobile sidebar here.
                                // Closing it unmounts Sidebar and the modal never renders.
                                setShowMockInterviewModal(true);
                            }
                        }}
                        active={location.pathname === "/practice-interview-questions" || location.pathname === "/employee/practice-interview-questions"}
                    />
                </ul>

                {/* Admin Dashboard Link - Only visible to admin users */}
                {isAdmin(user) && (
                    <ul
                        className={`flex flex-col gap-1 px-4 ${
                            (!isMobile && !isOpen) ? "px-2" : ""
                        }`}
                    >
                        <NavItem
                            icon={
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M12,7C13.4,7 14.8,8.6 14.8,10V11.5C15.4,11.9 15.8,12.6 15.8,13.3V16.5C15.8,17.9 14.4,19.3 13,19.3H11C9.6,19.3 8.2,17.9 8.2,16.5V13.3C8.2,12.6 8.6,11.9 9.2,11.5V10C9.2,8.6 10.6,7 12,7M12,8.2C11.2,8.2 10.5,8.7 10.5,9.5V11.5H13.5V9.5C13.5,8.7 12.8,8.2 12,8.2Z"/>
                                </svg>
                            }
                            label={(isMobile || isOpen) ? "Admin Dashboard" : ""}
                            tooltip="Admin Dashboard"
                            isSidebarOpen={isMobile || isOpen}
                            onClick={() => {
                                navigate("/admin/hpcsa-verification");
                                if (isMobile) {
                                    onToggle();
                                }
                            }}
                            active={location.pathname === "/admin/hpcsa-verification"}
                        />
                    </ul>
                )}
            </div>

            {/* Footer */}
            <footer className="mt-auto px-4 py-4 flex flex-col gap-3">
                {/* Divider line above Suggest Ideas */}
                <div className="border-t border-gray-600"></div>

                {/* Request Feature Button */}
                {(isMobile || isOpen) && (
                    <div className="mb-3">
                        <button 
                            className="w-full bg-[#ececec] hover:bg-white font-bold border border-[rgba(3,56,39,0.4)] text-[#061B16] rounded-xl px-4 py-2"
                            onClick={() => window.open("https://doctors-jobs.features.vote/board", "_blank")}
                        >
                            Request a feature
                        </button>
                    </div>
                )}

                {/* tokens display */}
                {/* desktop */}
                {(isMobile || isOpen) && (
                    <div className="flex bg-white rounded-lg border border-gray-300 p-4 flex-col gap-2">
                        <div className="flex items-center gap-2">
                            <span className="font-normal text-black">
                                CV Tokens
                            </span>
                            <div className="relative group">
                                <img
                                    alt="Info Question Mark"
                                    src="/infoQuestionmark.png"
                                    className="!w-[17px] !h-[17px]"
                                />
                                {/* Tooltip */}
                                <div className="absolute left-1/2 transform -translate-x-1/2 -top-25 hidden group-hover:block w-max px-4 py-4 text-sm text-[#1e1e1e] bg-gray-100 rounded-lg shadow-lg z-10">
                                    <ul className="list-disc list-inside space-y-1">
                                        <li>Sign-Up Bonus: 250 tokens</li>
                                        <li>CV Analysis: 250 tokens</li>
                                    </ul>
                                    <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-gray-100 rotate-45"></div>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-end space-x-2">
                            <span className="text-3xl font-medium text-black">
                                {tokens ?? 0}
                            </span>
                            <span className="text-sm text-gray-600">
                                tokens available
                            </span>
                        </div>
                        <button
                            className="bg-black text-white rounded-full py-2 font-medium hover:opacity-90"
                            onClick={() => setShowTokenModal(true)}
                        >
                           Buy CV Scan Tokens   
                        </button>
                    </div>
                )}

                {/* mobile */}
                {!isMobile && !isOpen && (
                    <div className="relative group w-8 h-8 flex rounded items-center justify-center ml-2">
                        <svg
                            className="w-8 h-8 transform -rotate-90"
                            viewBox="0 0 100 100"
                        >
                            <circle
                                cx="50"
                                cy="50"
                                r="35"
                                stroke="#4a5568"
                                strokeWidth="8"
                                fill="transparent"
                            />
                            <circle
                                cx="50"
                                cy="50"
                                r="35"
                                stroke="#ffffff"
                                strokeWidth="8"
                                fill="transparent"
                                strokeDasharray={`${
                                    (400 * (tokens ?? 0)) / 100
                                } 219.9`}
                                strokeLinecap="round"
                            />
                        </svg>

                        {/* Add button inside the circle */}
                        <div
                            className="absolute inset-0 flex items-center justify-center cursor-pointer"
                            onClick={() => setShowTokenModal(true)}
                        >
                            <span className="text-sm font-bold text-white flex items-start justify-start mb-0.5">
                                +
                            </span>
                        </div>

                        {/* Tooltip */}
                        {!isOpen && (
                            <ToolTip toolTipLabel="Buy Tokens" />
                        )}
                    </div>
                )}

                {/* User Profile Section */}
                {(isMobile || isOpen) && user && (
                    <div className="relative">
                        <div 
                            className="flex items-center gap-3 p-3 rounded-lg"
                            style={{ 
                                backgroundColor: '#3b4040',
                                border: '1px solid #6f6f72'
                            }}
                        >
                            {/* User Avatar */}
                            <div className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center">
                                <span className="text-white text-sm font-medium">
                                    {user.email ? user.email.charAt(0).toUpperCase() : 'U'}
                                </span>
                            </div>
                            
                            {/* User Info */}
                            <div className="flex-1 min-w-0">
                                <div className="text-white font-bold text-sm truncate">
                                    {user.user_metadata?.full_name || user.email?.split('@')[0] || 'User'}
                                </div>
                                <div className="text-white text-xs truncate">
                                    {user.email}
                                </div>
                            </div>
                            
                        </div>
                    </div>
                )}
                
                {/* Collapsed User Profile */}
                {!isMobile && !isOpen && user && (
                    <div className="relative group flex justify-center">
                        <div 
                            className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center"
                        >
                            <span className="text-white text-sm font-medium">
                                {user.email ? user.email.charAt(0).toUpperCase() : 'U'}
                            </span>
                        </div>
                        
                        {/* Tooltip */}
                        <ToolTip toolTipLabel={`${user.user_metadata?.full_name || user.email?.split('@')[0] || 'User'}`} />
                    </div>
                )}
            </footer>

            {/* Token Purchase Modal */}
            <TokenPurchaseModal
                isOpen={showTokenModal}
                onClose={() => setShowTokenModal(false)}
                onSuccess={handleTokenPurchaseSuccess}
            />

            {/* Mock Interview Modal */}
            <MockInterviewModal
                isOpen={showMockInterviewModal}
                showConsentOnly={mockInterviewPaid && !mockInterviewConsented}
                onClose={() => {
                    setShowMockInterviewModal(false);
                    // CRITICAL: Only navigate if user is Employee
                    if (profileInfo?.role === 'Employee') {
                        navigate("/employee/dashboard");
                    }
                }}
                onStartPracticing={async () => {
                    setShowMockInterviewModal(false);
                    // CRITICAL: Double-check role before navigation
                    if (profileInfo?.role !== 'Employee') {
                        console.warn('[EmployeeSidebar] Navigation blocked: User is not an Employee');
                        return;
                    }
                    // Refresh payment and consent status
                    try {
                        const { data } = await supabase
                            .from('user_tokens')
                            .select('mock_interview_paid, mock_interview_consent')
                            .eq('user_id', user.id)
                            .single();
                        
                        if (data) {
                            setMockInterviewPaid(data.mock_interview_paid || false);
                            setMockInterviewConsented(data.mock_interview_consent || false);
                        }
                    } catch (err) {
                        console.error("Error refreshing payment status:", err);
                    }
                    navigate("/employee/practice-interview-questions");
                }}
            />
        </nav>
    );
};

export default EmployeeSidebar;
